import { supabase } from './supabase';

// Public Files (OPIF bulk downloader) is currently limited to operators while the
// backend is hardened for general load. Gate both the page and its nav entry off
// this single source so real users never see a nav item that dead-ends in
// "Not Authorized". Widen this list (or replace with a plan/role check) to launch it.
export const PUBLIC_FILES_ALLOWED_EMAILS = ['nitintangellamudi@gmail.com'];
export function canUsePublicFiles(email?: string | null): boolean {
  return !!email && PUBLIC_FILES_ALLOWED_EMAILS.includes(email);
}

const BACKEND_URL = (
  (import.meta.env.VITE_RAG_API_BASE_URL as string | undefined) ||
  (import.meta.env.VITE_BACKEND_API_URL as string | undefined) ||
  'https://fcc-summarize-app-r4ac3.ondigitalocean.app'
).replace(/\/+$/, '');

async function getAuthToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...((init.headers as Record<string, string>) ?? {}),
  };
  const res = await fetch(`${BACKEND_URL}${path}`, { ...init, headers });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const msg =
      (data as Record<string, string>)?.detail ||
      (data as Record<string, string>)?.error ||
      `HTTP ${res.status}`;
    throw new Error(String(msg));
  }
  return res.json() as Promise<T>;
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PublicFilesJob {
  id: string;
  start_url: string;
  status: 'queued' | 'crawling' | 'downloading' | 'done' | 'failed';
  total_files: number;
  downloaded: number;
  failed_count: number;
  started_at: string;
  completed_at?: string;
  error?: string;
  zip_status?: 'queued' | 'zipping' | 'ready' | 'failed' | null;
  zip_size_bytes?: number | null;
  zip_error?: string | null;
}

export interface PublicFilesFile {
  /** Sanitized filename as stored in Supabase Storage */
  name: string;
  /** Signed download URL (1-hour expiry) */
  signed_url: string;
  /** Sub-folder within the job, e.g. "Political Files/2024" or "" for root */
  folder_path: string;
}

// ── API calls ─────────────────────────────────────────────────────────────────

export async function createPublicFilesJob(url: string): Promise<{ job_id: string }> {
  return apiFetch<{ job_id: string }>('/api/public-files/jobs', {
    method: 'POST',
    body: JSON.stringify({ url }),
  });
}

export async function getPublicFilesJob(jobId: string): Promise<PublicFilesJob> {
  return apiFetch<PublicFilesJob>(`/api/public-files/jobs/${jobId}`);
}

export async function listPublicFilesJobs(): Promise<PublicFilesJob[]> {
  const resp = await apiFetch<{ jobs: PublicFilesJob[] }>('/api/public-files/jobs');
  return Array.isArray(resp.jobs) ? resp.jobs : [];
}

export async function getPublicFilesJobFiles(
  jobId: string,
  limit = 100,
  offset = 0,
): Promise<PublicFilesFile[]> {
  interface RawFile {
    filename: string;
    path: string;
    signed_url: string;
  }
  const resp = await apiFetch<{ files: RawFile[] }>(
    `/api/public-files/jobs/${jobId}/files?limit=${limit}&offset=${offset}`,
  );
  const files = Array.isArray(resp.files) ? resp.files : [];
  return files.map((item) => ({
    name: item.filename,
    signed_url: item.signed_url,
    folder_path: _extractFolderPath(item.path, jobId),
  }));
}

export async function requestZipArchive(
  jobId: string,
): Promise<{ job_id: string; zip_status: string }> {
  return apiFetch<{ job_id: string; zip_status: string }>(
    `/api/public-files/jobs/${jobId}/zip`,
    { method: 'POST' },
  );
}

export async function getZipStatus(jobId: string): Promise<{
  zip_status: string | null;
  zip_size_bytes: number | null;
  zip_error?: string | null;
}> {
  return apiFetch<{
    zip_status: string | null;
    zip_size_bytes: number | null;
    zip_error?: string | null;
  }>(`/api/public-files/jobs/${jobId}/zip`);
}

/**
 * Build a streaming download URL for the job's ZIP.
 * The JWT is passed as a query param because window.location.href
 * navigation cannot set Authorization headers.
 */
export async function getZipDownloadUrl(jobId: string): Promise<string> {
  const token = await getAuthToken();
  const qs = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${BACKEND_URL}/api/public-files/jobs/${jobId}/download${qs}`;
}

/** Derive relative folder from the full storage path. */
function _extractFolderPath(fullPath: string, jobId: string): string {
  const prefix = `public-files/${jobId}/`;
  const rel = fullPath.startsWith(prefix) ? fullPath.slice(prefix.length) : fullPath;
  const parts = rel.split('/');
  return parts.length > 1 ? parts.slice(0, -1).join('/') : '';
}
